import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { BulkUploadResponse } from '../models/bulk-upload-response';
import { OnyxEvent } from '../models/event';
import { OnyxSummaryEvent } from '../models/event.summary';
import { RatePlanCodeReservationCounts } from '../models/rate-plan-code-reservation-counts';
import { RatePlanCodeValidationRequest } from '../models/rate-plan-code-validation-request';
import { StaySummary } from '../models/stay-summary';
import { StaySummaryRequest } from '../models/stay-summary-request';
import { ValidationError } from '../models/validation-error';
import { EventHttpService } from './event.http.service';

@Injectable()
export class EventStore {

  private mySelectedEvent: BehaviorSubject<OnyxEvent> = new BehaviorSubject(null);

  constructor(private eventHttpService: EventHttpService,
              private translateService: TranslateService,
              private userLocalStorageService: UserLocalStorageService,
              private flashMessagesService: FlashMessagesService) {
  }

  /**
   * Returns an observable containing the current event.
   * Used in any case when data needs to be shown on a screen.
   *
   * @returns {Observable<T>}
   */
  get selectedEvent(): Observable<OnyxEvent> {
    return this.mySelectedEvent.asObservable();
  }

  resetSelectedEvent() {
    this.mySelectedEvent.next(null);
  }

  /**
   * Retrieves event.  This method returns an observable
   * and should be used only in route resolvers.
   *
   * @param eventId
   * @returns {Observable<OnyxEvent>}
   */
  getEvent(eventId: number): Observable<OnyxEvent> {
    return this.eventHttpService.get(eventId)
      .catch((error: any) => {
        // Catch error here so resolver also displays error.
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving OnyxEvent: ${error}`);
        return Observable.throw(error);
      })
      .map((res: OnyxEvent) => {
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  /**
   * Retrieves event.  This method returns an observable
   * and should be used only in route resolvers.
   *
   * @param eventId
   * @param eventPartyId
   * @returns {Observable<OnyxEvent>}
   */
  getEventByParty(eventId: number, eventPartyId: number): Observable<OnyxEvent> {
    return this.eventHttpService.getByParty(eventId, eventPartyId)
      .catch((error: any) => {
        // Catch error here so resolver also displays error.
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving OnyxEvent: ${error}`);
        return Observable.throw(error);
      })
      .map((res: OnyxEvent) => {
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  setEvent(event: OnyxEvent) {
    this.mySelectedEvent.next(event);
  }

  addEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.add(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        setTimeout(() => {
          if (res.hasOoivExceptions) {
            this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
              {'role': 'Agency'}));
          }
        }, 2000);
        return res;
      });
  }

  addAgencyEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.addAsAgency(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        setTimeout(() => {
          if (res.hasOoivExceptions) {
            this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
              {'role': 'Agency'}));
          }
        }, 2000);
        return res;
      });
  }

  updateEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.update(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        setTimeout(() => {
          if (res.hasOoivExceptions) {
            this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
              {'role': 'Agency'}));
          }
        }, 2000);
        return res;
      });
  }

  updateAgencyEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.updateAsAgency(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        setTimeout(() => {
          if (res.hasOoivExceptions) {
            this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
              {'role': 'Agency'}));
          }
        }, 2000);
        return res;
      });
  }

  updateEventIndicators(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.updateIndicators(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  downloadInvalidEventsCsv(fileId: number): Observable<any> {
    return this.eventHttpService.downloadInvalidEventsCsv(fileId);
  }

  deleteEvent(eventId: number): Observable<OnyxEvent> {
    return this.eventHttpService.delete(eventId)
      .catch((error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_EVENT');
        return Observable.throw(error);
      }))
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.DELETE_EVENT');
        this.mySelectedEvent.next(null);
        return res;
      });
  }

  importEventsViaCsv(file: File): Observable<BulkUploadResponse> {
    return this.eventHttpService.importEventsViaCsv(file)
      .catch((error => {
        return Observable.throw(error);
      }))
      .map((importResponse: BulkUploadResponse) => {
        return importResponse;
      });
  }

  getStaySummary(event: OnyxEvent): Observable<StaySummary> {
    return this.eventHttpService.getStaySummary(event)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving stay summary: ${error}`);
        return Observable.throw(error);
      });
  }

  getVendorStaySummary(request: StaySummaryRequest): Observable<StaySummary> {
    return this.eventHttpService.getVendorStaySummary(request)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving vendor stay summary: ${error}`);
        return Observable.throw(error);
      });
  }

  getRatePlanCodeValidation(request: RatePlanCodeValidationRequest): Observable<ValidationError[]> {
    return this.eventHttpService.getRatePlanCodeValidation(request)
      .catch(error => {
          return Observable.throw(error);
        }
      );
  }

  getReservationCountForRatePlanCode(eventPartyId: number, ratePlanCode: string): Observable<RatePlanCodeReservationCounts> {
    return this.eventHttpService.getReservationCountForRatePlanCode(eventPartyId, ratePlanCode)
      .catch(error => {
        return Observable.throw(error);
      });
  }

  getEventStatus(eventId: number): Observable<string> {
    return this.eventHttpService.status(eventId)
      .catch((error: any) => {
        // Catch error here so resolver also displays error.
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT_STATUS', `Error retrieving OnyxSummaryEvent: ${error}`);
        return Observable.throw(error);
      })
      .map((res: OnyxSummaryEvent) => {
        return res.vendorsPaymentStatus;
      });
  }

  updateAutoPayment(event: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.updateAutoPayment(event)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/event.store.ts